<template>
  <div v-show="props.announcements?.length > 0 && isLibraryPage"
       class="announcement-banner-container"
       :class="{ 'is-centered': !showNavigation }"
       :style="currentBannerStyle"
       @mouseenter="pauseRotation"
       @mouseleave="resumeRotation">
    <transition name="fade"
                mode="out-in">
      <button v-show="showNavigation"
              class="nav-button prev"
              @click="prevAnnouncement">
        <span class="icon is-small">
          <i class="fas fa-chevron-left"></i>
        </span>
      </button>
    </transition>
    <transition v-if="!!currentAnnouncement"
                name="fade"
                mode="out-in">
      <div class="announcement-item mx-2"
           :class="{ 'is-clickable': isClickable(currentAnnouncement) }"
           :key="currentAnnouncement.id"
           @click="handleAnnouncementClick(currentAnnouncement)">
        <span class="announcement-title has-text-weight-bold"
              ref="titleSpanRef"
              :title="currentAnnouncement.title">
          {{ currentAnnouncement.title }}
        </span>
        <!-- Info icon for truncated titles on mobile/tablet -->
        <span v-show="shouldShowInfoIcon"
              class="icon is-small mx-2"
              @click.stop="showModal()">
          <i class="fas fa-info-circle"></i>
        </span>
      </div>
    </transition>
    <transition name="fade"
                mode="out-in">
      <button v-show="showNavigation"
              class="nav-button next"
              @click="nextAnnouncement">
        <span class="icon is-small">
          <i class="fas fa-chevron-right"></i>
        </span>
      </button>
    </transition>
  </div>
  <!-- Modal to display full title -->
  <base-modal v-if="isAnnouncementModalOpen"
              @close="closeModal">
    <template #title>
      <span :style="{ 'font-size': '1rem', 'font-weight': 'normal' }">
        {{ currentAnnouncement?.title }}
      </span>
    </template>
    <template #actions>
      <button class="button"
              @click="closeModal">
        <span>{{ i18n.global.t('close') }}</span>
      </button>
      <button v-if="currentAnnouncement ? isClickable(currentAnnouncement) : false"
              class="button is-primary"
              @click="handleAnnouncementClick(currentAnnouncement!!)">
        <span>{{ modalSubmitLabel }}</span>
      </button>
    </template>
  </base-modal>
</template>

<script setup lang="ts">
import BaseModal from '@/components/common/modal/BaseModal.vue'
import i18n from '@/locales'
import { AnnouncementLibraryResponse } from '@/interfaces/library/announcement'
import { AnnouncementType } from '@/interfaces/admin/announcement'
import { computed, onMounted, onUnmounted, PropType, ref, watchEffect } from 'vue'
import { customDownload } from '@/helpers/customDownload'
import { getMediaById } from '@/controllers/library'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useWidgetNavigator } from '@/components/widgets/composables/useWidgetNavigator'
import { WidgetRootBreadcrumbs } from '@/interfaces/global'

const props = defineProps({
  announcements: {
    type: Array as PropType<AnnouncementLibraryResponse[]>,
    required: true,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { navToViewWithLoading, setRootBreadcrumb } = useWidgetNavigator()
const currentIndex = ref(0);
const isPaused = ref(false); // Track hover state
const isWidget: boolean = store.getters['widgets/isWidget'];
let intervalId: number | undefined;

const libraryExceptions = [
  'Exporter',
  'JobManager',
  'Notification', // News Items
  'Notifications', // News Items
  'Order',
  'Orders',
  'OrderSubmitted',
  'Preferences', // Settings
  'PurchaseOrder',
  'Profile', // Settings
  'ReportsManager', // Data Insights
  'RequestForQuote'
];
const isLibraryPage = computed(() => {
  const currentRouteName = typeof route.name === 'string' ? route.name : '';
  const isLibrary = !route.path.startsWith('/admin');
  return isLibrary && !libraryExceptions.includes(currentRouteName);
});

const modalSubmitLabel = computed(() => {
  switch (currentAnnouncement.value?.type) {
    case AnnouncementType.FILE:
      return i18n.global.t('download')
    default:
      return i18n.global.t('open')
  }
})
const showNavigation = computed(() => (props.announcements?.length || 0) > 1);
const currentAnnouncement = computed<AnnouncementLibraryResponse | undefined>(() => {
  if (props.announcements && props.announcements.length > 0) {
    return props.announcements[currentIndex.value];
  }
  return undefined;
});

const defaultBannerColor = store.getters['theme/bannersColor'];
const defaultBannerColorInverted = store.getters['theme/bannersColorInverted'];
const currentBannerStyle = computed(() => {
  if (currentAnnouncement.value === undefined) {
    return {
      backgroundColor: defaultBannerColor,
      color: defaultBannerColorInverted
    }
  }

  return {
    backgroundColor: currentAnnouncement.value.barColor ?? defaultBannerColor,
    color: currentAnnouncement.value.textColor ?? defaultBannerColorInverted
  }
})

const titleSpanRef = ref(null);
const isTitleTruncated = ref(false);

// --- Viewport Detection ---
const checkViewport = () => {
  checkTitleTruncation(); // ensures reactivity when resizing window
  isAnnouncementModalOpen.value = false;
};

// --- Truncation + Viewport Condition ---
const shouldShowInfoIcon = computed(() => isTitleTruncated.value);

// --- Function to check truncation (called by transition hook) ---
const checkTitleTruncation = () => {
  if (titleSpanRef.value) {
    isTitleTruncated.value = titleSpanRef.value.scrollWidth > titleSpanRef.value.clientWidth;
  } else {
    isTitleTruncated.value = false;
  }
};

// --- Modal State & Handlers ---
const isAnnouncementModalOpen = ref(false);

const showModal = () => {
  pauseRotation();
  isAnnouncementModalOpen.value = true;
};

const closeModal = () => {
  resumeRotation();
  isAnnouncementModalOpen.value = false;
};

const stopRotation = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = undefined;
  }
};

const startRotation = () => {
  stopRotation(); // Clear any existing interval before starting
  // Start new interval only if there are multiple announcements and not paused
  if (showNavigation.value && !isPaused.value && !isAnnouncementModalOpen.value) {
    intervalId = setInterval(() => {
      currentIndex.value = (currentIndex.value + 1) % props.announcements.length;
    }, 8500); // Rotate every 8.5 seconds
  }
};

const resetTimer = () => {
  isTitleTruncated.value = false;
  startRotation(); // Simply restart the rotation, which includes clearing the old timer
};

const nextAnnouncement = () => {
  if (!props.announcements || props.announcements.length === 0) return;
  currentIndex.value = (currentIndex.value + 1) % props.announcements.length;
  resetTimer(); // Reset timer on manual navigation
};

const prevAnnouncement = () => {
  if (!props.announcements || props.announcements.length === 0) return;
  currentIndex.value = (currentIndex.value - 1 + props.announcements.length) % props.announcements.length;
  resetTimer(); // Reset timer on manual navigation
};

const pauseRotation = () => {
  isPaused.value = true;
  stopRotation();
};

const resumeRotation = () => {
  isPaused.value = false;
  startRotation();
};

const isClickable = (announcement: AnnouncementLibraryResponse): boolean => {
  return !!announcement.externalUrl || !!announcement.relativePath || !!announcement.targetMedia;
};

const handleAnnouncementClick = async (announcement: AnnouncementLibraryResponse) => {
  isAnnouncementModalOpen.value = false;

  switch (announcement.type) {
    case AnnouncementType.EXTERNAL:
      if (announcement.externalUrl) {
        window.open(announcement.externalUrl, '_blank', 'noopener,noreferrer');
      }
      break;
    case AnnouncementType.FILE:
      if (announcement.relativePath) {
        // Check if the path ends with '.pdf' (case-insensitive)
        if (announcement.relativePath.toLowerCase().endsWith('.pdf')) {
          // Open PDFs in a new tab
          window.open(announcement.relativePath, '_blank', 'noopener,noreferrer');
        } else {
          // For other file types, use custom download
          customDownload(announcement.relativePath);
        }
      }
      break;
    case AnnouncementType.MEDIA:
      if (announcement.targetMedia?.identifier) {
        switch (isWidget) {
          case true: {
            const mediaData = await getMediaById(announcement.targetMedia.id);
            setRootBreadcrumb(WidgetRootBreadcrumbs.search);
            await navToViewWithLoading(mediaData);
            break;
          }
          case false: {
            await router.push({
              name: 'MediaIdentifierRedirect',
              params: { id: announcement.targetMedia.identifier }
            });
            break;
          }
        }
      }
  }
  // If neither URL nor path is present, clicking does nothing
};

onMounted(async () => {
  startRotation();
  window.addEventListener('resize', checkViewport);
  checkViewport();
});

onUnmounted(() => {
  window.removeEventListener('resize', checkViewport);
  stopRotation();
});

watchEffect(() => {
  if (currentAnnouncement.value && titleSpanRef.value) {
    checkTitleTruncation();
  } else {
    isTitleTruncated.value = false;
  }
});
</script>

<style scoped lang="scss">
.announcement-banner-container {
  align-items: stretch;
  display: flex;
  min-height: 40px;
  justify-content: space-between;
  position: relative;
  transition: background-color 1.5s ease, color 0.2s ease;
  width: 100%;

  &.is-centered {
    justify-content: center;
  }
}

.announcement-item {
  display: flex;
  align-items: center;
  flex-grow: 1;
  min-width: 0;
  padding: 0.5rem 0.25rem;
  justify-content: center;
  text-align: center;

  margin-left: auto;
  margin-right: auto;

  &.is-clickable {
    cursor: pointer;
    &:hover {
      filter: brightness(95%);
    }
  }
}

@media screen and (min-width: 769px) {
  .announcement-item {
    max-width: 80vw;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.announcement-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  display: inline-block; /* Ensure clientWidth/scrollWidth work reliably */
  vertical-align: middle; /* Align nicely if needed */
}

.nav-button {
  background: transparent;
  border: none;
  color: currentColor;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 0.75rem;
  line-height: 1;
  opacity: 0.8;
  transition: color 0.5s ease, opacity 0.2s ease;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 1;
  }
}
</style>
